import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../api/axios";

/* ═══════════════════════════════════════════════════════════════════════════
   KEYS AND SECRETS

   The third-party credentials this shop runs on: the payment gateway, the SMS
   sender, the loyalty account, the camera. They are stored encrypted on the
   server and never sent back — a secret that can be read back out of a screen
   is a secret that leaks through a screenshot.

   ── WHY THE PAYMENT MODE IS THE LOUDEST THING HERE ──
   "Can this charge a real card?" is the one question on this page a person
   must never have to ask a developer, and the answer is already in the
   Razorpay key id: rzp_test_ or rzp_live_. So the page leads with it, in
   words, before anything else. Test mode cannot move real money — Razorpay
   runs it as a separate world with its own card numbers, and a real card is
   refused there. Live mode moves real money on the first tap.
   ═══════════════════════════════════════════════════════════════════════════ */

const MODE = {
  test: {
    title: "Test mode",
    line: "No real money moves. Razorpay refuses a real card here, and nothing reaches a bank account.",
    bg: "var(--mly-ok-soft)", fg: "var(--mly-ok)",
  },
  live: {
    title: "LIVE — real payments",
    line: "Every payment here is real money from a real card, settled to your bank account.",
    bg: "var(--mly-danger-soft)", fg: "var(--mly-danger)",
  },
  unset: {
    title: "No payment key",
    line: "Razorpay is not configured, so online payment cannot be taken at all. Cash on delivery still works.",
    bg: "var(--mly-warn-soft)", fg: "var(--mly-warn)",
  },
};

const S = {
  page:    { maxWidth: 860, margin: "0 auto", padding: "8px 0 96px", fontFamily: "var(--mly-font)", color: "var(--mly-text)" },
  title:   { margin: 0, fontFamily: "var(--mly-heading, var(--mly-font))", fontSize: 24, fontWeight: 800, letterSpacing: -0.2 },
  sub:     { margin: "6px 0 22px", color: "var(--mly-text-muted)", fontSize: 13, maxWidth: 640, lineHeight: 1.5 },
  banner:  { borderRadius: 14, padding: "16px 20px", marginBottom: 22, border: "1px solid var(--mly-border)" },
  bTitle:  { fontFamily: "var(--mly-heading, var(--mly-font))", fontSize: 18, fontWeight: 800, margin: 0 },
  bLine:   { margin: "4px 0 0", fontSize: 13.5, lineHeight: 1.5 },
  card:    { background: "var(--mly-surface)", border: "1px solid var(--mly-border)", borderRadius: 14, boxShadow: "var(--mly-shadow-sm)", overflow: "hidden", marginBottom: 18 },
  head:    { padding: "12px 16px", borderBottom: "1px solid var(--mly-border)", fontFamily: "var(--mly-heading, var(--mly-font))", fontWeight: 700, fontSize: 15 },
  row:     { padding: "14px 16px", borderBottom: "1px solid var(--mly-border-soft, var(--mly-border))", display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 12, alignItems: "start" },
  label:   { fontSize: 14, fontWeight: 600 },
  help:    { fontSize: 11.5, color: "var(--mly-text-muted)", marginTop: 2, lineHeight: 1.5 },
  state:   { fontFamily: "var(--mly-font-mono)", fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", padding: "3px 8px", borderRadius: 999, whiteSpace: "nowrap" },
  set:     { background: "var(--mly-ok-soft)", color: "var(--mly-ok)" },
  unset:   { background: "var(--mly-surface-2)", color: "var(--mly-text-muted)" },
  input:   { width: "100%", marginTop: 8, fontFamily: "var(--mly-font-mono)", fontSize: 13, padding: "8px 11px", borderRadius: 9, border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text)" },
  actions: { display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" },
  btn:     { fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, padding: "7px 13px", borderRadius: 9, border: "1px solid var(--mly-border)", background: "var(--mly-surface-2)", color: "var(--mly-text)", cursor: "pointer" },
  btnMain: { fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, padding: "7px 15px", borderRadius: 9, border: "1px solid var(--mly-accent)", background: "var(--mly-accent)", color: "var(--mly-on-accent)", cursor: "pointer" },
  off:     { opacity: 0.5, cursor: "not-allowed" },
  note:    { padding: "10px 14px", borderRadius: 10, background: "var(--mly-warn-soft)", fontSize: 13, marginBottom: 18, border: "1px solid var(--mly-border)" },
};

export default function Keys() {
  const [groups, setGroups] = useState([]);
  const [mode, setMode] = useState("unset");
  const [canStore, setCanStore] = useState(true);
  const [reason, setReason] = useState("");
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings/secrets");
      setGroups(data.groups || []);
      setMode(data.paymentMode || "unset");
      setCanStore(data.canStore !== false);
      setReason(data.reason || "");
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not load the keys");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (name) => {
    const value = (draft[name] || "").trim();
    if (!value) return;
    setBusy(name);
    try {
      await api.put(`/admin/settings/secrets/${name}`, { value });
      setDraft((d) => ({ ...d, [name]: "" }));
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not save");
    } finally { setBusy(""); }
  };

  const clear = async (name, label) => {
    if (!window.confirm(`Remove ${label}? The server falls back to whatever is in its environment.`)) return;
    setBusy(name);
    try {
      await api.delete(`/admin/settings/secrets/${name}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not remove");
    } finally { setBusy(""); }
  };

  const testRazorpay = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/admin/settings/secrets/test/razorpay", {});
      if (data.ok) toast.success(data.message || "Razorpay accepted the key");
      else toast.error(data.message || "Razorpay refused the key");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not reach Razorpay");
    } finally { setBusy(""); }
  };

  const m = MODE[mode] || MODE.unset;

  return (
    <div style={S.page}>
      <h1 style={S.title}>Keys &amp; Secrets</h1>
      <p style={S.sub}>
        The accounts this shop runs on. Values are stored encrypted and are never
        shown again — to change one, type the new value over it.
      </p>

      <div style={{ ...S.banner, background: m.bg }}>
        <p style={{ ...S.bTitle, color: m.fg }}>{m.title}</p>
        <p style={S.bLine}>{m.line}</p>
        {mode !== "unset" && (
          <div style={S.actions}>
            <button type="button" style={{ ...S.btn, ...(busy === "test" ? S.off : null) }}
              disabled={busy === "test"} onClick={testRazorpay}>
              {busy === "test" ? "Asking Razorpay…" : "Check the key with Razorpay"}
            </button>
          </div>
        )}
      </div>

      {!canStore && <div style={S.note}>{reason}</div>}

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "var(--mly-text-muted)" }}>Loading…</div>
      ) : (
        groups.map((g) => (
          <div key={g.group} style={S.card}>
            <div style={S.head}>{g.group}</div>
            {g.items.map((it) => (
              <div key={it.name} style={S.row}>
                <div style={{ minWidth: 0 }}>
                  <div style={S.label}>{it.label}</div>
                  <div style={S.help}>{it.help}</div>
                  {it.hint && <div style={{ ...S.help, color: "var(--mly-text)" }}>{it.hint}</div>}
                  <input
                    type={it.sensitive ? "password" : "text"}
                    style={S.input}
                    value={draft[it.name] || ""}
                    placeholder={it.isSet ? "Type a new value to replace it" : "Not set"}
                    onChange={(e) => setDraft((d) => ({ ...d, [it.name]: e.target.value }))}
                    disabled={!canStore}
                    autoComplete="off"
                  />
                  <div style={S.actions}>
                    <button type="button"
                      style={{ ...S.btnMain, ...(!canStore || !draft[it.name] || busy === it.name ? S.off : null) }}
                      disabled={!canStore || !draft[it.name] || busy === it.name}
                      onClick={() => save(it.name)}>
                      {busy === it.name ? "Saving…" : "Save"}
                    </button>
                    {it.source === "stored" && (
                      <button type="button" style={S.btn} disabled={busy === it.name}
                        onClick={() => clear(it.name, it.label)}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <span style={{ ...S.state, ...(it.isSet ? S.set : S.unset) }}>
                    {it.isSet ? (it.source === "stored" ? "Saved here" : "From the server") : "Not set"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
